/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/

import React, { useState, useEffect, useContext, useRef } from 'react';
import Icon from 'components/Icon';
import { FieldArray, Field, Form, Formik } from 'formik';
import * as yup from 'yup';
import {
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCollum,
  ViewText,
  ButtonContainer,
  ButtonContent,
  LabelEdit,
  InlineLabel
} from './styles';
import { GridColumnTitle, Label } from 'components/Form';
import CheckBox from "components/Form/CheckBox";
import Row from 'components/Grid/Row';
import Col from 'components/Grid/Col';
import ComboBox from 'components/Form/ComboBox3';
import InputField from 'components/Form/InputField';
import { FitinsurAPI, MessageCenter } from 'utils';
import Button from 'components/Button';
import { GlobalLoaderContext, GlobalAuthContext } from 'store/context';
import ModalConfirmDelete from 'components/ModalConfirmDelete';
import SearchField from 'components/Form/SearchField';

const ContactInformation = ({ searchList, setSearchList, searchCountrySubDivision, contactTypes, columns, data, countries, states, hasPermission, idPerson, onSaveContact, isHolding }) => {
  const formikRef = useRef(null);
  const contactDescriptionRef = useRef(null);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const [validations, setValidations] = useState({});
  const mandatoryCountriesIds = [17, 42, 123, 170, 194, 203, 221];

  const [sortBy, setSortBy] = useState({ key: null, direction: null });
  const [contactType, setContactType] = useState(null);
  const [addContact, setAddContact] = useState(false);
  const [idContact, setIdContact] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [changeFieldState, setChangeFieldState] = useState(false);
  const [isCountrySubDivisionRequired, setIsCountrySubDivisionRequired] = useState(false);

  const [initial, setInitial] = useState({
    shortName: '',
    fullName: '',
    personType: '',
    regulatoryId: '',
    legalId: '',
    legalIdType: {
      idDomain: '',
      domainDescription: '',
      idDomainType: '',
    },
    contactType: '',
    description: '',
    street: '',
    number: '',
    complement: '',
    zipCode: '',
    district: '',
    city: '',
    state: '',
    country: '',
    countrySubdivision: '',
  });


  const setEditContact = (contact) => {
    if (contact.country == "United States") setChangeFieldState(true);
    if (mandatoryCountriesIds.includes(contact.idCountry)) {
      setIsCountrySubDivisionRequired(true);
    } else {
      setIsCountrySubDivisionRequired(false);
    }
    formikRef.current.values.description = contact.description;
    formikRef.current.values.contactType = { idDomain: contact.idDomain, description: contact.contactType, idDomainType: contact.idDomainType, };
    formikRef.current.values.street = contact.street;
    formikRef.current.values.doorNumber = contact.doorNumber;
    formikRef.current.values.city = contact.city;
    formikRef.current.values.country = { countryId: contact.idCountry, countryName: contact.country, value: contact.idCountry, label: contact.country };
    formikRef.current.values.complement = contact.complement;
    formikRef.current.values.zipCode = contact.zipCode;
    formikRef.current.values.district = contact.district;
    formikRef.current.values.countrySubdivision = contact.countrySubdivision;
    const stateSelected = states.find(item => item.label == contact.state);
    formikRef.current.values.state = stateSelected ? { stateId: stateSelected.value.stateId, stateName: stateSelected.label, value: stateSelected.value.stateId, label: stateSelected.label } : contact.state;

    setAddContact(true);
    setIdContact(contact.idContact);
    setContactType(contact.idDomain);
    setTimeout(() => contactDescriptionRef.current && contactDescriptionRef.current.focus(), 100)
  }

  const resetForm = () => {
    formikRef.current.values.description = '';
    formikRef.current.values.contactType = '';
    formikRef.current.values.street = '';
    formikRef.current.values.doorNumber = '';
    formikRef.current.values.city = '';
    formikRef.current.values.country = '';
    formikRef.current.values.complement = '';
    formikRef.current.values.zipCode = '';
    formikRef.current.values.district = '';
    formikRef.current.values.state = '';
    formikRef.current.values.countrySubdivision = '';
  }

  const saveContact = async (body) => {
    body = JSON.parse(JSON.stringify(body));

    try {
      loading.start();
      body.idPerson = idPerson;
      body.idContact = idContact;

      if (body.countrySubdivision?.id) {
        body.countrySubdivision = body.countrySubdivision?.id;
      } else if (body.countrySubdivision === '' || !body.countrySubdivision) {
        body.countrySubdivision = null;
      }

      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSaveContact(body).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      setIdContact(null);
      setAddContact(false);
      resetForm();
      onSaveContact && onSaveContact();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }


  const deleteContact = async (idContact) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestDeleteContact(idContact).then(res => res.json());
      setIdContact(null);
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      onSaveContact && onSaveContact();
    } catch (err) {
      setIdContact(null);
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const setMainContact = async (idContact) => {
    try {
      loading.start();
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur.requestSetMainContact(idContact).then(res => res.json());
      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details)
        loading.stop();
        return;
      }

      onSaveContact && onSaveContact();
    } catch (err) {
      loading.stop();
      mc.error("An error happened, please try again.");
    };
  }

  const createValidation = () => {
    let validations = yup.object().shape({
      description: yup.string().notRequired(),
      contactType: yup.string().required("* Required field"),
      street: yup.string().notRequired(),
      number: yup.string().notRequired(),
      complement: yup.string().notRequired(),
      zipCode: yup.string().notRequired(),
      district: yup.string().notRequired(),
      city: yup.string().notRequired(),
      state: yup.string().notRequired(),
      country: contactType !== 30 ? yup.string().notRequired() : yup.string().required("* Required field"),
      countrySubdivision: isCountrySubDivisionRequired ? yup.string().required("* Required field") : yup.string().notRequired().nullable(),
    });
    setValidations(validations);
    return validations
  }

  useEffect(() => {
    createValidation();
  }, []);

  useEffect(() => {
    createValidation();
  }, [contactType]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initial}
      enableReinitialize={true}
      onSubmit={(values) => saveContact(values)}
      validationSchema={validations}
    >
      {({ setFieldValue, values }) => (
        <>
          <ModalConfirmDelete show={deleteModalOpen} onModalClose={() => { setDeleteModalOpen(false); setIdContact(null); }} onConfirm={() => deleteContact(idContact)} />
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((column, index) =>
                  <TableHeader right={column.right} key={index} minWidth={column.minWidth} maxWidth={column.maxWidth}>
                    <GridColumnTitle style={{ cursor: column.value ? 'pointer' : 'not-allowed' }}>
                      {column.label}
                    </GridColumnTitle>
                    {column.value && sortBy.key === column.value && sortBy.direction === "asc" ?
                      <Icon name={"arrowup"} /> :
                      column.value && sortBy.key === column.value && sortBy.direction === "desc" ?
                        <Icon name={"arrowdown"} /> :
                        ''}
                  </TableHeader>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              <FieldArray
                name="management"
                render={() =>
                  data.map((item, index) => (
                    <>
                      <TableRow key={index} >
                        <TableCollum>
                          <InlineLabel>
                            Main
                          </InlineLabel>
                          <label>
                            <CheckBox
                              name={item.idContact}
                              type="checkbox"
                              checked={item.isMainContact}
                              onChange={() => setMainContact(item.idContact)}
                            />
                          </label>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Contact Type
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.contactType}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Description
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.description}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Street
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.street}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Number
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.doorNumber}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            City
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.city}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Country
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.country}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum>
                          <InlineLabel>
                            Country <br /> Sub-Division
                          </InlineLabel>
                          <LabelEdit fontSize={12}>
                            {item.countrySubdivision?.value || ''}
                          </LabelEdit>
                        </TableCollum>

                        <TableCollum right>
                          <InlineLabel>
                            ACTION
                          </InlineLabel>
                          <Label>
                            <Icon
                              name="edit"
                              style={{ alignSelf: "center", cursor: "pointer" }}
                              onClick={() => setEditContact(item)}
                            />
                            <Icon
                              name="trash"
                              style={{ alignSelf: "center", cursor: "pointer" }}
                              onClick={() => {
                                if (isHolding && item.idDomain === 30) {
                                  const addressContacts = data.filter(e => e.idDomain === 30);
                                  if (item.isMainContact) {
                                    mc.error("Main address cannot be deleted from holdings.");
                                    return;
                                  }
                                  
                                  if (addressContacts.length === 1) {
                                    mc.error("Holdings must have at least one address contact.");
                                    return;
                                  }
                                }

                                setIdContact(item.idContact);
                                setDeleteModalOpen(true);
                                setAddContact(false);
                                resetForm();
                              }}
                            />
                          </Label>
                        </TableCollum>
                      </TableRow>
                    </>
                  ))
                }
              />
            </TableBody>
          </Table>


          {!addContact && <Row>
            <Col lg={6} md={6} sm={6} xs={12}></Col>
            <Col lg={6} md={6} sm={6} xs={12}>
              <ViewText style={{ marginTop: '15px' }}>
                <Icon name="plus" onClick={() => setAddContact(true)} style={{ cursor: 'pointer' }} />
              </ViewText>
            </Col>
          </Row>}


          {addContact &&
            <Form>
              <div style={{ marginTop: 40, marginBottom: 10 }}>
                <Row>
                  <Col lg={6} md={6} sm={6} xs={12}>
                    <Field
                      name="contactType"
                      label="Contact Type"
                      isRequired={true}
                      options={contactTypes || []}
                      searchList={contactTypes || []}
                      onChange={(e) => {
                        setFieldValue(`contactType`, e);
                        setContactType(e.idDomain);
                      }}
                      component={ComboBox}
                      disabled={!hasPermission || idContact}
                    />
                  </Col>
                  <Col lg={6} md={6} sm={6} xs={12}>
                    <Field
                      onInputRef={(ref) => contactDescriptionRef.current = ref}
                      name="description"
                      label="Description"
                      component={InputField}
                      value={values.description}
                      disabled={!hasPermission}
                    />
                  </Col>
                </Row>
                {contactType == 30 && <>
                  <Row>
                    <Col xs={12} sm={6} md={6} lg={6} xl={6}>
                      <Field
                        name="street"
                        label="Street"
                        component={InputField}
                        disabled={!hasPermission}
                      />
                    </Col>
                    <Col lg={6} md={6} sm={6} xs={12}>
                      <Field
                        name="doorNumber"
                        label="Number"
                        component={InputField}
                        disabled={!hasPermission}
                      />
                    </Col>
                  </Row>

                  <Row>
                    <Col lg={4} md={4} sm={4} xs={12}>
                      <Field
                        name="complement"
                        label="Complement"
                        component={InputField}
                        disabled={!hasPermission}
                      />
                    </Col>
                    <Col lg={4} md={4} sm={4} xs={12}>
                      <Field
                        name="zipCode"
                        label="Zip Code"
                        component={InputField}
                        disabled={!hasPermission}
                      />
                    </Col>
                    <Col lg={4} md={4} sm={4} xs={12}>
                      <Field
                        name="district"
                        label="District"
                        component={InputField}
                        disabled={!hasPermission}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={4} md={4} sm={4} xs={12}>
                      <Field
                        name="city"
                        label="City"
                        component={InputField}
                        disabled={!hasPermission}
                      />
                    </Col>
                    {
                      changeFieldState ?
                        <Col lg={4} md={4} sm={4} xs={12}>
                          <Field
                            name="state"
                            label="State"
                            component={ComboBox}
                            disabled={!hasPermission}
                            options={states || []}
                            searchList={states || []}
                            onChange={(e) => { setFieldValue(`state`, e); }}
                          />
                        </Col>
                        : <Col lg={4} md={4} sm={4} xs={12}>
                          <Field
                            name="state"
                            label="State"
                            component={InputField}
                            disabled={!hasPermission}
                          />
                        </Col>
                    }
                    <Col lg={4} md={4} sm={4} xs={12}>
                      <Field
                        name="country"
                        label="Country"
                        isRequired={contactType && contactType === 30 ? true : false}
                        options={countries || []}
                        searchList={countries || []}
                        onChange={(e) => {
                          setFieldValue(`country`, e);
                          if (e.countryId === 221) {
                            setChangeFieldState(true)
                          } else {
                            setChangeFieldState(false)
                          }
                          setFieldValue(`state`, '')
                          setFieldValue(`countrySubdivision`, '')

                          if (mandatoryCountriesIds.includes(e.countryId)) {
                            setIsCountrySubDivisionRequired(true)
                          } else {
                            setIsCountrySubDivisionRequired(false)
                          }
                          setFieldValue(`country`, e);
                        }}
                        component={ComboBox}
                        disabled={!hasPermission}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col lg={4} md={4} sm={4} xs={12}>
                      <Field
                        name="countrySubdivision"
                        label="Country Sub-Division"
                        component={SearchField}
                        isRequired={isCountrySubDivisionRequired}
                        disabled={!values.country?.countryId}
                        searchList={searchList}
                        value={values.countrySubdivision?.value || values.countrySubdivision || ''}
                        onChange={(value) => {
                          searchCountrySubDivision(values.country?.countryId, value);
                        }}
                        onFocus={() => {
                          setSearchList([]);
                          searchCountrySubDivision(values.country?.countryId);
                        }}
                        canGenerate={false}
                        onSearchSelect={(selectedValue) => {
                          setFieldValue(`countrySubdivision`, selectedValue);
                          setSearchList([]);
                        }}

                      />
                    </Col>
                  </Row>
                </>}

                <ButtonContainer>
                  <ButtonContainer style={{ justifyContent: 'flex-start', alignItems: 'center', paddingBottom: '10px' }} />
                  <ButtonContent>
                    <Button
                      fullWidth
                      style={{ marginLeft: 15 }}
                      third
                      onClick={() => {
                        setIdContact(null);
                        setAddContact(false);
                        resetForm();
                      }}
                    >
                      CANCEL
                    </Button>
                  </ButtonContent>
                  <ButtonContent>
                    <Button
                      fullWidth
                      style={{ marginLeft: 15 }}
                      type="submit"
                      primary
                      onClick={() => {
                        setFieldValue(`state`, values.state && values.state.stateName ? values.state.stateName : values.state)
                        createValidation().validate(values).catch((e) => {
                          console.log('validation error', e)
                          mc.error('Please fill in all required fields.')
                        })
                      }}
                      id="btnSubmit">
                      {idContact ? "SAVE CONTACT" : "ADD CONTACT"}
                    </Button>
                  </ButtonContent>
                </ButtonContainer>
              </div>
            </Form>}
        </>
      )}
    </Formik>
  )
}

export default ContactInformation;